/**
 * GraphNodeDetail — Slide-out panel showing detailed info about a graph node.
 */

import { X, ArrowRight, ArrowLeft, Eye, Sparkles, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ProcessGraph, GraphNode, AiConfig } from '../types';
import type { ModelEntity } from '@/lib/api';

const TYPE_LABELS: Record<string, string> = {
  'user-input': 'User Input',
  'ai-process': 'AI Process',
  code: 'Code',
  'entity-ref': 'Entity Reference',
  'arche-ref': 'Arche Reference',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'text-muted-foreground' },
  building: { label: 'Building', color: 'text-yellow-600 dark:text-yellow-400' },
  verified: { label: 'Verified', color: 'text-green-600 dark:text-green-400' },
};

const TIER_COLORS: Record<string, string> = {
  fast: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  balanced: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  powerful: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
};

interface GraphNodeDetailProps {
  node: GraphNode;
  graph: ProcessGraph;
  aiConfigs?: AiConfig[];
  /** Model definitions for resolving display names from modelDefinitionId */
  modelDefinitions?: ModelEntity[];
  onClose: () => void;
  onPreviewNode?: (nodeId: string) => void;
  onEditConfig?: (configId: string) => void;
}

export function GraphNodeDetail({
  node,
  graph,
  aiConfigs = [],
  modelDefinitions = [],
  onClose,
  onPreviewNode,
  onEditConfig,
}: GraphNodeDetailProps) {
  // Find upstream and downstream nodes
  const upstreamEdges = graph.edges.filter((e) => e.target === node.id);
  const downstreamEdges = graph.edges.filter((e) => e.source === node.id);
  const upstreamNodes = upstreamEdges
    .map((e) => graph.nodes.find((n) => n.id === e.source))
    .filter(Boolean) as GraphNode[];
  const downstreamNodes = downstreamEdges
    .map((e) => graph.nodes.find((n) => n.id === e.target))
    .filter(Boolean) as GraphNode[];

  const status = STATUS_LABELS[node.status] ?? STATUS_LABELS.pending;

  function resolveModel(modelDefinitionId: string) {
    return modelDefinitions.find((m) => m.entityId === modelDefinitionId);
  }

  return (
    <div className="absolute right-0 top-0 bottom-0 w-80 bg-background border-l shadow-lg overflow-y-auto z-10">
      <div className="sticky top-0 bg-background border-b px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold truncate">{node.title}</h3>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {/* Type and status */}
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-medium">
            {TYPE_LABELS[node.type] ?? node.type}
          </span>
          <span className={cn('text-xs font-medium', status.color)}>{status.label}</span>
        </div>

        {/* Preview Screen button — shown for nodes with UI (user-input type or components) */}
        {onPreviewNode && (node.type === 'user-input' || node.components.length > 0) && (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => onPreviewNode(node.id)}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview this screen
          </Button>
        )}

        {/* Description */}
        <div>
          <p className="text-xs text-muted-foreground">{node.description}</p>
        </div>

        {/* Input ports */}
        {node.inputs.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1 flex items-center gap-1">
              <ArrowRight className="h-3 w-3" /> Inputs
            </h4>
            <div className="space-y-1">
              {node.inputs.map((port) => (
                <div key={port.id} className="text-xs flex items-center gap-2">
                  <span className="font-mono text-muted-foreground">{port.dataType}</span>
                  <span>{port.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Output ports */}
        {node.outputs.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1 flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Outputs
            </h4>
            <div className="space-y-1">
              {node.outputs.map((port) => (
                <div key={port.id} className="text-xs flex items-center gap-2">
                  <span className="font-mono text-muted-foreground">{port.dataType}</span>
                  <span>{port.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upstream connections */}
        {upstreamNodes.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1">Receives data from</h4>
            <div className="space-y-1">
              {upstreamNodes.map((n) => (
                <div key={n.id} className="text-xs text-muted-foreground">
                  {n.title}
                  <span className="ml-1 opacity-60">({n.type})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Downstream connections */}
        {downstreamNodes.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold mb-1">Sends data to</h4>
            <div className="space-y-1">
              {downstreamNodes.map((n) => (
                <div key={n.id} className="text-xs text-muted-foreground">
                  {n.title}
                  <span className="ml-1 opacity-60">({n.type})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Build details */}
        {(node.components.length > 0 || node.actions.length > 0 || node.aiConfigIds.length > 0) && (
          <div className="border-t pt-3">
            <h4 className="text-xs font-semibold mb-2">Implementation Details</h4>
            {node.components.length > 0 && (
              <div className="mb-1">
                <span className="text-[10px] text-muted-foreground uppercase">Components</span>
                <p className="text-xs">{node.components.join(', ')}</p>
              </div>
            )}
            {node.actions.length > 0 && (
              <div className="mb-1">
                <span className="text-[10px] text-muted-foreground uppercase">Actions</span>
                <p className="text-xs font-mono">{node.actions.join(', ')}</p>
              </div>
            )}
            {node.aiConfigIds.length > 0 && (
              <div className="mb-1">
                <span className="text-[10px] text-muted-foreground uppercase">AI Configs</span>
                <div className="mt-1 space-y-2">
                  {node.aiConfigIds.map((configId) => {
                    const config = aiConfigs.find((c) => c.configId === configId);
                    if (!config) {
                      return (
                        <p key={configId} className="text-xs font-mono text-muted-foreground">
                          {configId}
                        </p>
                      );
                    }
                    const model = resolveModel(config.modelDefinitionId);
                    const tier = model?.tier;
                    const tierColor = tier ? TIER_COLORS[tier] : '';
                    return (
                      <div key={configId} className="rounded border p-2 bg-muted/50">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-purple-500" />
                            <span className="text-xs font-medium">{config.label}</span>
                          </div>
                          {onEditConfig && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => onEditConfig(configId)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          {tier && (
                            <span className={`text-[10px] px-1 py-0.5 rounded ${tierColor}`}>
                              {tier}
                            </span>
                          )}
                          <span className="text-[10px] font-mono text-muted-foreground">
                            {model?.name ?? config.modelDefinitionId}
                          </span>
                        </div>
                        {config.systemPrompt && (
                          <p
                            className="text-[10px] text-muted-foreground line-clamp-2"
                            title={config.systemPrompt}
                          >
                            {config.systemPrompt.slice(0, 100)}
                            {config.systemPrompt.length > 100 ? '...' : ''}
                          </p>
                        )}
                        <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                          {config.parameters.temperature != null && (
                            <span>temp: {config.parameters.temperature}</span>
                          )}
                          {config.parameters.maxTokens != null && (
                            <span>max: {config.parameters.maxTokens}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
